import Pill from 'ext-ui/components/Pill/Pill'
import ArrowIcon from 'ext-ui/svg/icon/ArrowIcon'
import React, { ReactElement } from 'react'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import NewInvitePill from 'src/feature/NewInvitePill/NewInvitePill'
import { useTranslate } from 'src/localization/Trns'
import { orgRoleDisplayTitle } from 'src/utils/roles.utils'

import type { AssignmentVariant } from '../ConfirmCardAssignmentModal'
import * as style from './RecipientHeader.style'

const initialsFor = (displayName: string | null, email: string): string => {
  if (displayName) {
    return displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
  }
  return email.charAt(0).toUpperCase()
}

export interface RecipientHeaderProps {
  variant: AssignmentVariant
  email: string
  name?: string | null
  avatarUrl?: string | null
  currentRole?: OrganizationRoles | null
}

const RecipientHeader: React.FC<RecipientHeaderProps> = ({
  variant,
  email,
  name,
  avatarUrl,
  currentRole,
}): ReactElement => {
  const translate = useTranslate()

  const displayName = name?.trim() || null

  return (
    <div css={style.container}>
      <div css={style.identity}>
        <UserAvatar
          size={AvatarSizes.Large}
          user={{
            id: email,
            fullNameOrEmail: displayName ?? email,
            initials: initialsFor(displayName, email),
            avatarUrl: avatarUrl ?? undefined,
          }}
        />
        <div css={style.text}>
          {displayName ? (
            <>
              <span css={style.name}>{displayName}</span>
              <span css={style.email}>{email}</span>
            </>
          ) : (
            <span css={style.name}>{email}</span>
          )}
        </div>
      </div>
      <div css={style.pills}>
        {variant === 'newInvite' && <NewInvitePill />}
        {variant === 'promote' && currentRole && (
          <>
            <Pill>{translate(orgRoleDisplayTitle(currentRole))}</Pill>
            <ArrowIcon direction="right" aria-hidden="true" css={style.arrow} />
            <Pill>{translate('Card Manager')}</Pill>
          </>
        )}
        {variant === 'elevated' && currentRole && (
          <Pill>{translate(orgRoleDisplayTitle(currentRole))}</Pill>
        )}
      </div>
    </div>
  )
}

export default RecipientHeader
